import { Download, Loader2 } from 'lucide-react';

export type RenderGridItem = {
  id: string;
  state: 'rendering' | 'failed' | 'ready';
  videoUrl: string | null;
  /** Photo trends: the finished image (takes precedence over `videoUrl`). */
  imageUrl?: string | null;
  /** Background behind the placeholder while rendering. */
  swatch?: string;
  caption: string;
};

const ASPECT = {
  tall: 'aspect-[9/16]',
  portrait: 'aspect-[3/4]',
} as const;

/** The user's renders: tiles with a spinner, a failure note, or the video/image + download. */
export function RenderGrid({
  title,
  items,
  labels,
  aspect = 'tall',
  onDownload,
}: {
  title: string;
  items: readonly RenderGridItem[];
  labels: { rendering: string; failed: string; download: string; waitHint?: string };
  /** `tall` = 9:16 videos, `portrait` = 3:4 images. */
  aspect?: keyof typeof ASPECT;
  /** Fires when the visitor takes a finished render (the delivery moment). */
  onDownload?: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="border-t border-border pt-8">
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      {labels.waitHint && items.some((item) => item.state === 'rendering') ? (
        <p className="mb-3 rounded-md bg-muted px-3 py-2 text-xs leading-5 text-muted-foreground">{labels.waitHint}</p>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((item) => {
          const mediaUrl = item.state === 'ready' ? item.imageUrl || item.videoUrl : null;
          return (
            <div key={item.id} className="overflow-hidden rounded-lg border border-border bg-card">
              <div className={`relative ${ASPECT[aspect]}`} style={{ background: item.swatch }}>
                {mediaUrl && item.imageUrl ? (
                  <img src={mediaUrl} alt={item.caption} className="size-full object-cover" />
                ) : mediaUrl ? (
                  <video src={mediaUrl} controls playsInline preload="metadata" className="size-full bg-black object-cover" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/35 p-3 text-center text-xs font-medium text-white">
                    {item.state === 'failed' ? (
                      <span>{labels.failed}</span>
                    ) : (
                      <>
                        <Loader2 className="size-5 animate-spin" aria-hidden="true" />
                        <span>{labels.rendering}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-1 p-2">
                <p className="truncate text-xs text-muted-foreground">{item.caption}</p>
                {mediaUrl ? (
                  <a
                    href={mediaUrl}
                    download
                    onClick={onDownload}
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    <Download className="size-3.5" aria-hidden="true" />
                    {labels.download}
                  </a>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
